<template>
  <div class="oai-WebsiteStep">
    <OnboardingAiStepHeader
      :subtitle="$t('onboarding.ai.website.subtitle')"
      :title-key-accent="$t('onboarding.ai.website.title_accent')"
      :wizard-step="wizardStep"
      title-key-name="withAi"
      title-key-path="onboarding.ai.website.title_prefix"
    />

    <div class="oai-WebsiteStep_Form">
      <AppInput
        v-model="localWebsite"
        :class="{ 'oai-WebsiteStep_Input-error': showWebsiteError }"
        :is-error="showWebsiteError"
        :placeholder="$t('onboarding.ai.website.placeholder')"
        autocomplete="url"
        class="oai-WebsiteStep_Input"
        icon="globe"
        size="6xlg"
        @blur="onWebsiteBlur"
        @focus="onWebsiteFocus"
        @keydown.enter.prevent="onEnterKey"
      />

      <OnboardingAiStepActions :disable="!canProceed" @next="handleNext" />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

import { useOnboardingAiTracking } from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { isStringEmpty, isUrl } from '@/utils/general'

import AppInput from '@/components/ui/AppInput/AppInput'
import OnboardingAiStepActions from '@/components/ui/onboarding-ai/OnboardingAiStepActions'
import OnboardingAiStepHeader from '@/components/ui/onboarding-ai/OnboardingAiStepHeader'

defineOptions({
  name: 'OnboardingAiWebsiteStep'
})

defineProps({
  wizardStep: {
    type: Number,
    required: true
  },

  modelValue: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  next: null
})

const { logEvent } = useOnboardingAiTracking()

const localWebsite = defineModel({ type: String, default: '' })

const trimmedWebsite = computed(() => localWebsite.value.trim())
const isWebsiteValid = computed(
  () => !isStringEmpty(trimmedWebsite.value) && isUrl(trimmedWebsite.value)
)

const hasValidatedWebsite = ref(false)

const showWebsiteError = computed(() => hasValidatedWebsite.value && !isWebsiteValid.value)

const canProceed = computed(() => isWebsiteValid.value)

const onWebsiteBlur = () => {
  hasValidatedWebsite.value = true
}

const onWebsiteFocus = () => {
  hasValidatedWebsite.value = false
}

const onEnterKey = () => {
  handleNext()
}

const handleNext = () => {
  hasValidatedWebsite.value = true

  if (!canProceed.value) {
    return
  }

  emit('next', {
    domain: trimmedWebsite.value
  })
}

onMounted(() => {
  logEvent(EVENT_NAMES.ONBOARDING_COMPANY_WEBSITE_VIEWED)
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';

.oai-WebsiteStep {
  @extend %oai-wizard-step-shell;

  padding-top: 0;
}

.oai-WebsiteStep_Form {
  display: grid;
  gap: 24px;
  width: 100%;
}

.oai-WebsiteStep_Input {
  width: 100%;
  --ob-ai-padding-primary: 16px;
  --ob-ai-border-radius-primary: #{$border-radius-md};
  --ob-ai-border-color-primary: #{$primary-color-next};
  --prepend-padding-left: 0;
  --input-with-prepend-padding-left: 16px;
  --ob-ai-box-shadow: #{$oai-input-shadow};
  --ob-ai-prepend-icon-color: #{$oai-text-action};
  --ob-ai-placeholder-color: #{$oai-text-placeholder};
  --ob-ai-input-field-color: #{$oai-text-primary};

  &-error {
    --ob-ai-box-shadow: none;
  }
}
</style>
